
import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ReadingPlanDay } from "@/hooks/useReadingPlan";

interface ReadingPlanDaysViewerProps {
  readingPlan: { readingPlan: ReadingPlanDay[] };
  topic: string;
  showSaveButton?: boolean;
  onSave?: () => void;
  isSaving?: boolean;
}

const ReadingPlanDaysViewer: React.FC<ReadingPlanDaysViewerProps> = ({
  readingPlan,
  topic,
  showSaveButton = false,
  onSave,
  isSaving
}) => {
  const [selectedDayIndex, setSelectedDayIndex] = useState<number | null>(0);

  const renderDayContent = (day: ReadingPlanDay) => (
    <div className="text-left">
      {day.scripture && (
        <div className="mb-4">
          <h4 className="text-md font-medium text-heaven-navy mb-1">Scripture</h4>
          <p className="text-gray-700 italic">{day.scripture}</p>
          <Separator className="my-4 bg-heaven-gold/10" />
        </div>
      )}
      <div className="mb-4">
        <h4 className="text-md font-medium text-heaven-navy mb-1">Commentary</h4>
        <p className="text-gray-700">{day.commentary}</p>
      </div>
      <div className="mb-4">
        <h4 className="text-md font-medium text-heaven-navy mb-1">Action Steps</h4>
        <ul className="list-disc pl-5 text-gray-700">
          {day.actionSteps.map((step, i) => (
            <li key={i} className="mb-1">{step}</li>
          ))}
        </ul>
      </div>
      <div>
        <h4 className="text-md font-medium text-heaven-navy mb-1">Prayer Points</h4>
        <ul className="list-disc pl-5 text-gray-700">
          {day.prayerPoints.map((point, i) => (
            <li key={i} className="mb-1">{point}</li>
          ))}
        </ul>
      </div>
    </div>
  );

  return (
    <div className="mt-8">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-playfair text-heaven-gold font-semibold text-left">
          {readingPlan.readingPlan.length}-Day Reading Plan: {topic}
        </h2>
        {showSaveButton && onSave && (
          <Button
            onClick={onSave}
            disabled={isSaving}
            className="bg-heaven-gold hover:bg-heaven-dark-gold text-white"
          >
            {isSaving ? "Saving..." : "Save to Dashboard"}
          </Button>
        )}
      </div>
      <div className="flex flex-wrap gap-2 my-4">
        {readingPlan.readingPlan.map((day, index) => (
          <Button
            key={index}
            variant={selectedDayIndex === index ? "gold" : "outline"}
            className={`text-sm px-3 py-1 ${selectedDayIndex === index ? "text-white" : "text-heaven-gold"}`}
            onClick={() => setSelectedDayIndex(index)}
          >
            {day.day.replace('Day ', '')}
          </Button>
        ))}
      </div>
      {selectedDayIndex !== null && (
        <Card className="border-heaven-gold/10 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center mb-4">
              <Button 
                variant="ghost" 
                className="mr-2 p-1"
                onClick={() => setSelectedDayIndex(null)}
              >
                <ChevronLeft className="h-5 w-5 text-heaven-gold" />
              </Button>
              <div>
                <h3 className="text-lg font-semibold text-heaven-navy text-left">
                  {readingPlan.readingPlan[selectedDayIndex].day}
                </h3>
                <p className="text-sm text-gray-600 text-left">
                  {readingPlan.readingPlan[selectedDayIndex].passage}
                </p>
              </div>
            </div>
            <Separator className="my-4 bg-heaven-gold/10" />
            {renderDayContent(readingPlan.readingPlan[selectedDayIndex])}
          </CardContent>
        </Card>
      )}
      {selectedDayIndex === null && readingPlan.readingPlan.length > 0 && (
        <div className="flex justify-center items-center h-32 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <p className="text-gray-500">Select a day from above to view the reading plan</p>
        </div>
      )}
    </div>
  );
};

export default ReadingPlanDaysViewer;
